import PropTypes from 'prop-types';

import { useCallback, useRef, useState } from 'react';

import Icon from 'components/global/Icon';

import useUpdateEffect from 'hooks/useUpdateEffect';

import { faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';

import Typography from '../global/Typography';
import Button from './Button';
import ViewField from './ViewField';
import * as S from './styled/Chips.styled';

const Chips = (props) => {
	const { value, setValue, viewMode, disabled, autoFocus = false } = props;

	const [localValue, setLocalValue] = useState(new Set(value ?? []));
	const [inputValue, setInputValue] = useState(null);
	const inputRef = useRef(null);
	const wrapperRef = useRef(null);

	useUpdateEffect(() => {
		setLocalValue(new Set(value ?? []));
	}, [value]);

	useUpdateEffect(() => {
		if (JSON.stringify([...localValue]) === JSON.stringify(value)) return;
		setValue([...localValue]);
	}, [localValue]);

	const handleFocus = useCallback(() => {
		inputRef.current.focus();
	}, []);

	const handleDelete = useCallback((item) => {
		setLocalValue((prev) => new Set([...prev].filter((x) => x !== item)));
		handleFocus();
	}, []);

	const handleRemoveButton = useCallback(
		(e, item) => {
			e.stopPropagation();
			handleDelete(item);
		},
		[handleDelete]
	);

	const handleAdd = useCallback((value) => {
		setLocalValue((prev) => (Array.isArray(value) ? new Set([...prev, ...value]) : new Set(prev.add(value))));
	}, []);

	const handlePaste = useCallback(
		(event) => {
			const clipboardData = event.clipboardData.getData('text');
			let values;
			if (clipboardData.search('\n') > -1) {
				values = clipboardData.split('\n');
			} else {
				values = clipboardData.split(',');
			}
			handleAdd(values.map((str) => str.trim()));
			event.preventDefault();
		},
		[handleAdd]
	);

	const handleAddButton = useCallback(
		(e) => {
			e.stopPropagation();

			const inputVal = inputValue?.trim();

			if (inputVal) {
				handleAdd(inputVal);
				setInputValue(null);
				handleFocus();
			}
		},
		[inputValue, handleAdd, handleFocus]
	);

	const handleKeyDown = useCallback(
		(e) => {
			const inputVal = e.target.value?.trim();
			if (e.key === 'Enter' && inputVal) {
				e.stopPropagation();
				handleAdd(inputVal);
				setInputValue(null);
			} else if (e.key === 'Backspace' && !inputVal && localValue?.size > 0) {
				e.stopPropagation();
				handleDelete([...localValue].pop());
			}
		},
		[handleAdd, handleDelete, localValue]
	);

	return viewMode || disabled ? (
		<ViewField value={value?.join(', ')} disabled={disabled} />
	) : (
		<S.Container>
			<S.InputWrapper>
				<S.Wrapper ref={wrapperRef} onClick={handleFocus}>
					{[...localValue]?.map((item, index) => (
						<S.Token key={index}>
							<Typography>{item}</Typography>
							<Icon icon={faXmark} onClick={(e) => handleRemoveButton(e, item)} />
						</S.Token>
					))}
					<S.InputToken>
						<S.Input
							ref={inputRef}
							autoFocus={autoFocus}
							onPaste={handlePaste}
							value={inputValue}
							setValue={setInputValue}
							onKeyDown={handleKeyDown}
						/>
					</S.InputToken>
				</S.Wrapper>
				<Button icon={faPlus} onClick={handleAddButton} variant="borderless" css={S.AddButtonCss} />
			</S.InputWrapper>
		</S.Container>
	);
};

Chips.propTypes = {
	/* Value for the chips component. */
	value: PropTypes.array,
	/* Fired when value changes. */
	setValue: PropTypes.func,
	/* View mode for the chips component. */
	viewMode: PropTypes.bool,
	/* Disables the chips component. */
	disabled: PropTypes.bool,
	/* Focuses the chips component. */
	autoFocus: PropTypes.bool
};

export default Chips;
